import { useState } from 'react';
import {
  Button, Chip, IconButton, Stack, TextField, MenuItem, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { prApi, type Pr, type PrItemInput } from '../../api/procurement.api';
import { omsApi } from '../../api/oms.api';
import { boqApi, type BoqItem } from '../../api/boq.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default', pending_approval: 'warning', approved: 'success', rejected: 'error', rfq: 'info',
  po_issued: 'info', in_transit: 'info', partially_received: 'warning', received: 'success', closed: 'default', cancelled: 'error',
};

export function PrListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [omsId, setOmsId] = useState<number | ''>('');
  const [boqId, setBoqId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [rows, setRows] = useState<Array<BoqItem & { requestQty: number }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Pr | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['prs', page, search], queryFn: () => prApi.list({ page, limit: 25, search: search || undefined }) });
  const omsOptions = useQuery({ queryKey: ['oms', 'forPr'], queryFn: () => omsApi.list({ limit: 100 }), enabled: createOpen });
  const boqOptions = useQuery({ queryKey: ['boqs', 'forPr', omsId], queryFn: () => boqApi.list({ limit: 100, omsId: Number(omsId) }), enabled: createOpen && !!omsId });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['prs'] });

  const loadBoqItems = async (id: number) => {
    setBoqId(id);
    const full = await boqApi.get(id);
    const cur = (full as { revisions?: { id: number; isCurrent: boolean }[] }).revisions?.find((r) => r.isCurrent);
    if (!cur) { setRows([]); setError('Selected BOQ has no approved revision'); return; }
    const rev = await boqApi.getRevision(id, cur.id);
    setRows(rev.items.map((it) => ({ ...it, requestQty: 0 })));
    setError(null);
  };

  const createMut = useMutation({
    mutationFn: () => {
      const items: PrItemInput[] = rows.filter((r) => r.requestQty > 0).map((r) => ({ boqItemId: r.id as number, description: r.description, unit: r.unit, quantity: r.requestQty }));
      return prApi.create({ omsId: Number(omsId), boqId: Number(boqId), title, items });
    },
    onSuccess: () => { setCreateOpen(false); setOmsId(''); setBoqId(''); setTitle(''); setRows([]); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, action }: { id: number; action: string }) => prApi.changeStatus(id, action),
    onSuccess: invalidate,
    onError: (e) => setToast(extractApiError(e)),
  });
  const deleteMut = useMutation({ mutationFn: (id: number) => prApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const columns: Column<Pr>[] = [
    { key: 'code', label: 'PR No.', width: 150 },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] ?? 'default'} /> },
  ];

  return (
    <>
      <PageHeader title="Purchase Requisitions" action={hasPermission('pr.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setCreateOpen(true); setError(null); }}>New PR</Button>} />
      <Stack direction="row" mb={2}><TextField size="small" placeholder="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} /></Stack>
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={25} total={data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id}
        actions={(r) => (
          <Stack direction="row" spacing={0.5}>
            {r.status === 'draft' && hasPermission('pr.update') && <Button size="small" onClick={() => statusMut.mutate({ id: r.id, action: 'submit' })}>Submit</Button>}
            {r.status === 'pending_approval' && hasPermission('pr.approve') && (
              <>
                <Button size="small" color="success" onClick={() => statusMut.mutate({ id: r.id, action: 'approve' })}>Approve</Button>
                <Button size="small" color="error" onClick={() => statusMut.mutate({ id: r.id, action: 'reject' })}>Reject</Button>
              </>
            )}
            <Tooltip title="Activity"><span><IconButton size="small" disabled><Visibility fontSize="small" /></IconButton></span></Tooltip>
            {hasPermission('pr.delete') && ['draft', 'rejected', 'cancelled'].includes(r.status) && <IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton>}
          </Stack>
        )}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>New Purchase Requisition</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="OMS" value={omsId} onChange={(e) => { setOmsId(Number(e.target.value)); setBoqId(''); setRows([]); }} fullWidth>
              {(omsOptions.data?.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.omsNumber} — {o.title}</MenuItem>)}
            </TextField>
            <TextField select label="Approved BOQ" value={boqId} onChange={(e) => loadBoqItems(Number(e.target.value))} disabled={!omsId} fullWidth>
              {(boqOptions.data?.data ?? []).map((b) => <MenuItem key={b.id} value={b.id}>{b.code} — {b.title}</MenuItem>)}
            </TextField>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
            {rows.length > 0 && (
              <Table size="small">
                <TableHead><TableRow><TableCell>Description</TableCell><TableCell>Unit</TableCell><TableCell>BOQ Qty</TableCell><TableCell>Request Qty</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.description}</TableCell><TableCell>{r.unit}</TableCell><TableCell>{r.quantity}</TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number" value={r.requestQty}
                          onChange={(e) => setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, requestQty: Number(e.target.value) } : x)))} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!omsId || !boqId || !title || !rows.some((r) => r.requestQty > 0)} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete PR" message={`Delete ${deleting?.code}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
